import {
  ORDER_STATUS_ENUM,
  ORDER_TYPE_ENUM,
  type OrderType,
} from "@/constants/enums";
import type {
  Order,
  CartLine,
  MenuCategory,
  MenuItem,
  SelectedAddress,
  SelectedTable,
  StaffStamp,
  StoreSettings,
} from "@/types";
import type { OrderTotals } from "@/utils/pricing";
import type { QueuedOrder } from "./offlineStore";

/**
 * BUILDING THE ORDER DOCUMENT AN OFFLINE ORDER SYNCS AS.
 *
 * PRODUCT CHANGE §2 (`DEVIATIONS.md`).
 *
 * ## Why this file has to exist at all
 *
 * `POST /web/orders` takes a LIGHT payload — a basket, an address, a tip — and
 * builds the stored document itself, re-pricing every line from the catalog.
 * `POST /orders/:tenantId/offline-sync` does not: it takes the FINISHED
 * document and upserts it (`mongo-api/src/routes/orders.js`), assigning only
 * `_id`, `tenant_id`, `order_number` and the payments it derives. So an order
 * that was taken with no connection has to arrive already shaped, and this is
 * the one place that shaping happens.
 *
 * The shape is a field-for-field mirror of the document
 * `mongo-api/src/controllers/web/orders.js` writes on the online path, so an
 * order that went through the queue and an order that did not are the same
 * document on the POS board.
 *
 * ## THE MONEY RULE
 *
 * Every figure below comes from the `OrderTotals` object the checkout ALREADY
 * rendered — the same one that printed the number on the CTA. It is not
 * recomputed here, and it must not be: the storefront has already shipped one
 * bug where the displayed and the charged total differed, and re-deriving the
 * total a second time from the same inputs is exactly how that happens again.
 *
 * `grand_total` is `Math.round(totals.total * 100)`. The customer saw
 * `totals.total`. There is one conversion between them and it is this one.
 *
 * ## The trade-off, stated rather than hidden
 *
 * On the online path the SERVER re-prices from the catalog, so a tampered
 * client cannot set its own total. An offline order is priced by the device,
 * because there is nothing else to price it. That is acceptable for the case
 * this feature exists for — the shop's own counter or a customer's phone with
 * the catalog the API served it — and it is not acceptable as a general
 * bypass, which is why an order is only ever queued when the network actually
 * failed (see `queueOrderOffline` in `offlineSync.ts`) and never as a
 * first choice.
 */

/* -------------------------------------------------------------------------- */
/* ids                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * A 24-character hex ObjectId, minted in the browser.
 *
 * **This is deliberately NOT a UUID, and that matters.** The brief for this
 * work said to key each order on its local UUID so a replay cannot create a
 * second order — the intent is exactly right and this is the shape that
 * delivers it, because of what the server does with the field:
 *
 * ```js
 * const _id = (o._id && mongoose.Types.ObjectId.isValid(o._id)) ? o._id : newId();
 * await Order.findByIdAndUpdate(_id, { $set: {...} }, { upsert: true })
 * ```
 *
 * The upsert on a client-minted `_id` IS the idempotency key — post the same
 * document twice and the second write overwrites the first. But a value that
 * is not a valid ObjectId is silently replaced with a FRESH id, so a UUID
 * would defeat the very mechanism it was meant to use: every replay would
 * create another order. `order_number` is likewise preserved across a replay
 * only because the server can find the stored document by this id.
 *
 * So the durable local key is an ObjectId. A `crypto.randomUUID()` is kept
 * alongside it as `local_uuid` for local bookkeeping and React keys, which is
 * what the POS does too — its `renderPostOfflineOrder` mints a real ObjectId
 * for `_id` and keeps a per-install UUID in `offline_metadata`.
 *
 * Layout is Mongo's: 4-byte seconds, 5-byte random per process, 3-byte
 * counter. Two tabs cannot collide on the random half, and one tab cannot
 * collide with itself on the counter.
 */
const PROCESS_RANDOM = (() => {
  const bytes = new Uint8Array(5);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 5; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
})();

let objectIdCounter = Math.floor(Math.random() * 0xffffff);

export const mintObjectId = (): string => {
  const timestamp = Math.floor(Date.now() / 1000)
    .toString(16)
    .padStart(8, "0");
  objectIdCounter = (objectIdCounter + 1) % 0xffffff;
  const counter = objectIdCounter.toString(16).padStart(6, "0");
  return `${timestamp}${PROCESS_RANDOM}${counter}`;
};

export const mintLocalUuid = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
};

/* -------------------------------------------------------------------------- */
/* money                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * `toCents` — byte-for-byte the server's own
 * (`controllers/web/orders.js`: `Math.round(num(major) * 100)`). Everything on
 * this platform persists in cents.
 */
const toCents = (major: number | null | undefined): number => {
  const value = Number(major);
  return Number.isFinite(value) ? Math.round(value * 100) : 0;
};

/* -------------------------------------------------------------------------- */
/* the document                                                               */
/* -------------------------------------------------------------------------- */

export interface BuildOfflineOrderInput {
  tenantId: string;
  orderType: OrderType;
  items: CartLine[];
  note: string;
  scheduledFor: string | null;
  address: SelectedAddress | null;
  table: SelectedTable | null;
  settings: StoreSettings;
  catalog: Record<string, MenuItem>;
  categories: MenuCategory[];
  customer: { name?: string; phone?: string; email?: string } | null;
  orderTakerDetails: StaffStamp | null;
  /** THE numbers the checkout displayed. Never recomputed here. */
  totals: OrderTotals;
}

/**
 * `platform` — `WEB_TAKE-AWAY` / `WEB_DELIVERY` / `WEB_DINE-IN`, exactly the
 * server's `` `WEB_${orderType}` `` and exactly the hyphenated values in
 * `PLATFORMS_ENUM`. The printer's `selectedPlatforms` gate and
 * `auto_accept_order_for_platforms` both compare against this, so the spelling
 * is load-bearing rather than cosmetic.
 */
export const platformFor = (orderType: OrderType): string =>
  `WEB_${String(orderType).toUpperCase()}`;

export const buildOfflineOrderDocument = ({
  tenantId,
  orderType,
  items,
  note,
  scheduledFor,
  address,
  table,
  settings,
  catalog,
  categories,
  customer,
  orderTakerDetails,
  totals,
}: BuildOfflineOrderInput): { _id: string; document: Record<string, unknown> } => {
  const _id = mintObjectId();
  const isDelivery = orderType === ORDER_TYPE_ENUM.DELIVERY;
  const isDineIn = orderType === ORDER_TYPE_ENUM.DINE_IN;
  const platform = platformFor(orderType);

  const categoryName = Object.fromEntries(
    categories.map((category) => [category._id, category.name ?? ""]),
  );

  /**
   * Group the basket into `categories[].items[]`, the platform's order shape.
   *
   * Line money mirrors `priceLines`: `unit` is the bare item price in cents,
   * `subtotal` is `(unit + options) * quantity`. The client's own
   * `unitPrice()`/`lineTotal()` arithmetic is the same expression, so the sum
   * of these lines is `totals.subtotal` — asserted below rather than assumed.
   */
  const grouped: Record<
    string,
    { category_id: string | null; name: string; items: unknown[] }
  > = {};

  for (const line of items) {
    const item = catalog[line.itemId];
    const categoryId = item?.category_id ?? "UNCATEGORISED";
    const bucket = (grouped[categoryId] ||= {
      category_id: item?.category_id ?? null,
      name: categoryName[categoryId] ?? "",
      items: [],
    });

    const quantity = Math.max(1, Math.round(Number(line.quantity) || 1));
    const unit = toCents(line.price);
    const modifiers = line.modifiers ?? [];
    const modifiersTotal = modifiers.reduce(
      (sum, modifier) =>
        sum +
        (modifier.is_free ? 0 : toCents(modifier.price)) *
          Math.max(1, Math.round(Number(modifier.quantity) || 1)),
      0,
    );
    const gross = (unit + modifiersTotal) * quantity;

    bucket.items.push({
      _id: mintObjectId(),
      item_id: String(line.itemId),
      // NULL, never the synthesised `${tenantId}-default-menu`. That value is
      // what made every offline-sync call 400 until the server started
      // coercing it: it is not castable to an ObjectId, and the order schema
      // declares `menu_id` as one. The server now nulls an uncastable value at
      // the top level and inside `iterations[]`, but sending a shape it has to
      // repair is not the same as sending a correct one.
      menu_id: null,
      item_type: "ITEM",
      is_ready: false,
      name: item?.name || line.name || "",
      price: unit,
      subtotal: gross,
      // The catalog `/web/menu` serves carries no `tax_rate`, so there is
      // nothing to copy; the online path reads it off the raw catalog document
      // and this tenant's items do not set it either. `tax_amount` on the
      // order is 0 on both paths.
      tax_rate: 0,
      quantity,
      _gst_amount: 0,
      _net_amount: gross,
      _gross_amount: gross,
      _discount_amount: 0,
      special_instructions: line.note || "",
      modifiers: modifiers.length
        ? [
            {
              _id: mintObjectId(),
              name: "",
              options: modifiers.map((modifier) => ({
                _id: String(modifier._id),
                // `ref` is what the ingredient-consumption index is keyed on;
                // dropping it would stop an option's recipe being deducted.
                ref: String(modifier._id),
                name: modifier.name || "",
                price: modifier.is_free ? 0 : toCents(modifier.price),
                quantity: Math.max(
                  1,
                  Math.round(Number(modifier.quantity) || 1),
                ),
                is_free: Boolean(modifier.is_free),
              })),
            },
          ]
        : [],
    });
  }

  const [firstName, ...rest] = String(customer?.name || "")
    .trim()
    .split(/\s+/);

  /**
   * A STAFF-placed order is already accepted — the person who took it is
   * standing at the counter — so it syncs as PLACED. A customer order syncs as
   * UNCONFIRMED and the POS raises its Accept prompt, which is what
   * `auto_accept_order_for_platforms` means for a WEB platform on this tenant.
   *
   * This decision belongs to the SERVER on the online path
   * (`lib/orderAcceptance.js`) and `offline-sync` does not run it, so it is
   * mirrored here — the one place in this app that duplicates a server rule,
   * and it is duplicated because the endpoint stores what it is given.
   */
  const order_status = orderTakerDetails?.staff_id ? "PLACED" : "UNCONFIRMED";

  const document: Record<string, unknown> = {
    _id,
    tenant_id: tenantId,
    platform,
    order_status,
    // No `order_number`: the server assigns one on sync, and preserves an
    // already-assigned one on a replay.
    consumer: {
      first_name: firstName || "",
      last_name: rest.join(" "),
      phone: customer?.phone || "",
      email: customer?.email || "",
    },
    categories: Object.values(grouped),
    special_instructions: note || "",
    payments: [],
    price: toCents(totals.subtotal),
    grand_total: toCents(totals.total),
    amount_earned: toCents(totals.total),
    tip_amount: toCents(totals.tip),
    gst: totals.gstBasisPoints,
    gst_amount: toCents(totals.tax),
    is_gst_inclusive: Boolean(settings.is_gst_inclusive),
    service_fee: Number(settings.dinein_service_fee ?? 0),
    service_fee_amount: toCents(totals.serviceFee),
    is_service_fee_percentage: Boolean(settings.is_service_fee_percentage),
    surcharge: Number(settings.web_holiday_surcharge ?? 0),
    surcharge_amount: toCents(totals.surcharge),
    is_surcharge_percentage: Boolean(settings.web_opt_in_for_holiday_surcharge),
    packaging_charges: toCents(totals.packaging),
    total_discount_applied: 0,
    tax_amount: 0,
    delivery_details:
      isDelivery && address
        ? {
            street: address.line1 || "",
            address_instructions: address.instructions || "",
            city: address.city || "",
            state: address.state || "",
            zip_code: address.zipCode || "",
            country_code: address.countryCode || settings.country_alpha_2 || "PK",
            lat: address.lat,
            lng: address.lng,
            delivery_cost: toCents(totals.delivery),
            delivery_code: "",
            distance_in_meters_from_store: address.distanceInMeters ?? 0,
            // The server re-runs `enrichDeliveryDistance` on sync, which
            // re-resolves the road distance and stamps the polyline — so this
            // is the last-known figure, not the final one.
            estimated_delivery_time: new Date(
              Date.now() + Number(settings.delivery_time ?? 0),
            ).toISOString(),
            // Null, not `{}`: the POS's "Assign Rider" control is gated on
            // `rider_details?.staff_id == null`, and an empty-string id reads
            // as "already assigned" and hides it permanently.
            rider_details: null,
          }
        : null,
    dinein_details:
      isDineIn && table
        ? {
            floor_id: table.floorId ?? null,
            table_id: table.tableId ?? null,
            table_name: table.tableName || "",
            num_of_guests: Number(table.guests) || 1,
            start_time: new Date().toISOString(),
            reservation_time: scheduledFor || new Date().toISOString(),
            courses: null,
          }
        : null,
    staff_details: null,
    order_taker_details: orderTakerDetails ?? null,
    scheduled_for: scheduledFor,
    // Stamped so the POS board shows the time the customer actually ordered,
    // not the time the connection came back.
    createdAt: new Date().toISOString(),
    order_status_updated_at: new Date().toISOString(),
  };

  return { _id, document };
};

/**
 * THE PRINTABLE view of the same queued order — MAJOR units.
 *
 * ## Why this is a separate builder and not a conversion of the document
 *
 * `buildOfflineOrderDocument` above produces the document that is POSTED to
 * `offline-sync`, and that document is in CENTS because that is what the
 * platform stores. `PrintableReceipt` renders through `localeAmount`, which
 * deliberately does NOT divide — its contract is MAJOR units, and its other
 * call site (`OrderReceiptPanel`, fed by `/web/orders/:id`) already satisfies
 * it. Handing the cents document to that component prints every figure 100×
 * over; that exact mistake is live on the thermal path today (see
 * `src/lib/printing.ts`).
 *
 * So the receipt is built from `totals` DIRECTLY — the same object the checkout
 * CTA printed its own figure from, already in major units. There is no
 * conversion on this path at all, in either direction, which is the only way to
 * be sure the printed total and the displayed total cannot drift.
 *
 * It is also, emphatically, NOT a mutation of the queued document: that object
 * is the sync payload, and rescaling it in place would turn a display bug into
 * a money-loss bug.
 *
 * `offlineOrderDocument.test.ts` pins the relationship — every money field here
 * is exactly `document field / 100`.
 *
 * ## What it deliberately does not claim
 *
 * No `order_number`: the server assigns one on sync, and printing a number the
 * shop has not issued would be a fabrication. `reference` carries the honest
 * placeholder the receipt header falls back to, and the caller additionally
 * passes a `notice` that the restaurant has not received the order yet.
 */
export const buildOfflineOrderReceipt = (
  input: BuildOfflineOrderInput,
  _id: string,
): Order => {
  const { totals } = input;
  return {
    _id,
    reference: "not yet sent",
    tenant_id: input.tenantId,
    order_type: input.orderType,
    status: ORDER_STATUS_ENUM.PLACED,
    placed_at: new Date().toISOString(),
    items: input.items,
    note: input.note,
    customer: input.customer
      ? {
          name: input.customer.name,
          phone: input.customer.phone,
          email: input.customer.email,
        }
      : null,
    platform: platformFor(input.orderType),
    subtotal: totals.subtotal,
    delivery: totals.delivery,
    packaging: totals.packaging,
    service_fee: totals.serviceFee,
    surcharge: totals.surcharge,
    // BASIS POINTS — a RATE, not money, and the one numeric field on this
    // object that must never be scaled. `PrintableReceipt` renders it as
    // `(gst / 100)%`, so 800 is 8%; running it through a money conversion
    // would print 8% as 0.08%.
    gst: totals.gstBasisPoints,
    tax: totals.tax,
    tip_amount: totals.tip,
    total: totals.total,
  };
};

/** The row `offlineStore` persists, summary and all. */
export const buildQueuedOrder = (
  input: BuildOfflineOrderInput,
): QueuedOrder => {
  const { _id, document } = buildOfflineOrderDocument(input);
  return {
    _id,
    local_uuid: mintLocalUuid(),
    tenant_id: input.tenantId,
    queued_at: Date.now(),
    attempts: 0,
    last_error: null,
    lan_relay_enabled: input.settings.web_staff_lan_offline_orders_enabled === true,
    summary: {
      order_type: String(input.orderType),
      item_count: input.items.reduce(
        (sum, line) => sum + Math.max(1, Math.round(Number(line.quantity) || 1)),
        0,
      ),
      // The SAME cents figure as `document.grand_total`, read off the document
      // rather than recomputed, so the banner and the stored order cannot
      // disagree either.
      grand_total: Number(document.grand_total ?? 0),
      currency_symbol: input.settings.currency_symbol ?? "",
    },
    document,
  };
};
